/**
 * PlanfinderPage — Geführter Planfinder-Wizard
 *
 * 2 Fragen + Ergebnisschritt (3 Schritte gesamt).
 * Nutzt audienceRecommendations aus pricing.js als einzige Empfehlungsquelle —
 * keine zweite Recommendation-Engine.
 *
 * Flow:
 *   Schritt 1 → Wer bist du?         (5 Audience-Karten)
 *   Schritt 2 → Was ist dir wichtig? (4 Prioritäts-Karten)
 *   Schritt 3 → Deine Empfehlung    (Plan + Add-ons + CTA)
 *
 * Übergang zur PricingPage:
 *   /pricing?highlight=<planId>&audience=<audienceKey>
 *   → PricingPage liest beide Params und aktiviert Ring + Audience-Filter.
 *
 * Persistenz:
 *   Nur React-State — kein localStorage-Wizard-State.
 *   Der PlanfinderBanner auf dem Dashboard nutzt einen eigenen dismiss-Key.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronRight, RotateCcw } from "lucide-react";
import {
  audienceRecommendations,
  getPlanById,
  getAddonById,
  trialConfig,
} from "../data/pricing";

// ── Schritt 1: Zielgruppen-Optionen ──────────────────────────────────────────
//
// Keys entsprechen exakt den Keys in audienceRecommendations.

const AUDIENCE_OPTIONS = [
  {
    key: "local_business",
    icon: "🏪",
    label: "Lokales Unternehmen",
    hint: "Restaurant, Laden, Dienstleister vor Ort",
  },
  {
    key: "coach_consultant",
    icon: "💼",
    label: "Coach / Berater",
    hint: "Einzelperson oder kleines Team, Expertise verkaufen",
  },
  {
    key: "creator",
    icon: "🎥",
    label: "Creator / Influencer",
    hint: "Content als Kerngeschäft, mehrere Plattformen",
  },
  {
    key: "education_provider",
    icon: "🎓",
    label: "Bildungsanbieter",
    hint: "Kurse, Seminare, Weiterbildungsangebote",
  },
  {
    key: "agency",
    icon: "🏢",
    label: "Agentur",
    hint: "Mehrere Kunden, Team, volle Skalierung",
  },
];

// ── Schritt 2: Prioritäts-Optionen ───────────────────────────────────────────
//
// Beeinflussen nicht die Empfehlung, sondern den situativen Hinweistext im Ergebnis.

const PRIORITY_OPTIONS = [
  {
    key: "reach",
    icon: "📣",
    label: "Mehr Reichweite",
    hint: "Regelmäßig auf mehreren Kanälen präsent sein",
  },
  {
    key: "efficiency",
    icon: "⚡",
    label: "Effizienter arbeiten",
    hint: "Weniger Zeit für Content-Erstellung aufwenden",
  },
  {
    key: "local",
    icon: "📍",
    label: "Lokal besser gefunden werden",
    hint: "Google, Maps, lokale Suchanfragen bedienen",
  },
  {
    key: "scale",
    icon: "📈",
    label: "Skalieren / mehrere Marken",
    hint: "Mehrere Kunden, Brands oder Standorte verwalten",
  },
];

// ── Situativer Prioritäts-Hinweis ─────────────────────────────────────────────
//
// Ergänzt die note aus audienceRecommendations — gibt dem Nutzer einen
// zusätzlichen, kontext-spezifischen Impuls. Kein zweites Empfehlungssystem.

function getPriorityNote(priorityKey, audienceKey) {
  if (priorityKey === "local" && audienceKey !== "local_business") {
    // Befund 19.08.2026 (§5 UWG): Hier stand „Das Google Unternehmensprofil gehört zu
    // den Kanälen deines Pakets". Es gehört zu keinem Paket — Google hat dem Projekt
    // für die Business-Profile-API kein Kontingent erteilt (quota_limit_value 0 seit
    // 18.07.2026). Die Sperre gilt für JEDEN Mandanten und ist von uns nicht lösbar.
    return "Tipp: Für lokale Sichtbarkeit spielen wir dir regelmäßige Beiträge auf deinen verbundenen Kanälen aus. Das Google Unternehmensprofil ist vorbereitet, aber noch nicht freigeschaltet — sobald Google es öffnet, kommt es ohne Aufpreis dazu.";
  }
  if (
    priorityKey === "scale" &&
    (audienceKey === "coach_consultant" || audienceKey === "education_provider")
  ) {
    return "Wenn dein Content-Bedarf steigt, ist der Wechsel auf Wachstum oder Dominanz jederzeit möglich.";
  }
  if (priorityKey === "reach" && audienceKey === "agency") {
    return "Für Agenturen als Enterprise-Paket: Team-Zugang, getrennte Kunden-Workspaces und individuelle Volumina — auf Anfrage zugeschnitten.";
  }
  return null;
}

// ── OptionCard ────────────────────────────────────────────────────────────────

const OptionCard = ({ icon, label, hint, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-start gap-4 p-4 rounded-xl border-2 border-stone-200 bg-white hover:border-fox-fire hover:bg-orange-50 transition-all text-left group"
  >
    <span className="text-2xl mt-0.5 flex-shrink-0">{icon}</span>
    <div>
      <p className="font-semibold text-stone-800 group-hover:text-fox-fire transition-colors text-sm">
        {label}
      </p>
      <p className="text-xs text-stone-400 mt-0.5 leading-snug">{hint}</p>
    </div>
  </button>
);

// ── ProgressBar ───────────────────────────────────────────────────────────────

const ProgressBar = ({ step, total }) => (
  <div className="flex items-center gap-2">
    {Array.from({ length: total }, (_, i) => i + 1).map((s) => (
      <div key={s} className="flex items-center gap-2">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
            s < step
              ? "bg-emerald-500 text-white"
              : s === step
              ? "bg-fox-fire text-white"
              : "bg-stone-200 text-stone-400"
          }`}
        >
          {s < step ? <Check size={11} /> : s}
        </div>
        {s < total && (
          <div
            className={`h-0.5 w-12 transition-all ${
              s < step ? "bg-emerald-400" : "bg-stone-200"
            }`}
          />
        )}
      </div>
    ))}
    <span className="text-xs text-stone-400 ml-1">
      Schritt {Math.min(step, total)} von {total}
    </span>
  </div>
);

// ── PlanfinderPage ────────────────────────────────────────────────────────────

const TOTAL_QUESTION_STEPS = 2;

export default function PlanfinderPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [audienceKey, setAudienceKey] = useState(null);
  const [priorityKey, setPriorityKey] = useState(null);

  // Schritt 1 → 2: Zielgruppe gewählt
  const handleAudienceSelect = (key) => {
    setAudienceKey(key);
    setStep(2);
  };

  // Schritt 2 → 3: Priorität gewählt
  const handlePrioritySelect = (key) => {
    setPriorityKey(key);
    setStep(3);
  };

  // Ergebnis → PricingPage mit Deep-Link + Audience-Param
  const handleGoToPricing = () => {
    const rec = audienceRecommendations[audienceKey];
    if (!rec) return;
    navigate(`/pricing?highlight=${rec.primaryPlan}&audience=${audienceKey}`);
  };

  // Zurück-Navigation
  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
      setAudienceKey(null);
    } else {
      navigate(-1);
    }
  };

  // Neu starten
  const handleReset = () => {
    setStep(1);
    setAudienceKey(null);
    setPriorityKey(null);
  };

  // Abgeleitete Werte für Schritt 3
  const recommendation = audienceKey ? audienceRecommendations[audienceKey] : null;
  const recommendedPlan = recommendation ? getPlanById(recommendation.primaryPlan) : null;
  const suggestedAddons =
    recommendation?.suggestedAddons?.map(getAddonById).filter(Boolean) ?? [];
  const priorityNote =
    audienceKey && priorityKey ? getPriorityNote(priorityKey, audienceKey) : null;
  const audienceOption = AUDIENCE_OPTIONS.find((o) => o.key === audienceKey);

  // ── Titel / Untertitel je Schritt ─────────────────────────────────────────
  const titles = {
    1: "Wer bist du?",
    2: "Was ist dir am wichtigsten?",
    3: "Deine Empfehlung",
  };
  const subtitles = {
    1: "Damit wir den passenden Plan für dich finden können.",
    2: "Wir zeigen dir, welcher Plan am besten zu deinen Zielen passt.",
    3: "Basierend auf deinen Angaben — du kannst den Plan jederzeit wechseln.",
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div>
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 mb-6 transition-colors"
        >
          <ArrowLeft size={14} />
          {step === 1 ? "Zur Übersicht" : "Zurück"}
        </button>

        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">🦊</span>
          <span className="text-xs font-semibold text-fox-fire uppercase tracking-wider">
            Planfinder
          </span>
        </div>

        <h1 className="font-heading text-2xl font-bold text-stone-900">
          {titles[step]}
        </h1>
        <p className="text-stone-500 mt-1 text-sm">{subtitles[step]}</p>
      </div>

      {/* ── Fortschrittsanzeige (nur während Fragen) ─────────────────────── */}
      {step <= TOTAL_QUESTION_STEPS && (
        <ProgressBar step={step} total={TOTAL_QUESTION_STEPS} />
      )}

      {/* ── Schritt 1: Zielgruppe ─────────────────────────────────────────── */}
      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {AUDIENCE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.key}
              icon={opt.icon}
              label={opt.label}
              hint={opt.hint}
              onClick={() => handleAudienceSelect(opt.key)}
            />
          ))}
        </div>
      )}

      {/* ── Schritt 2: Priorität ─────────────────────────────────────────── */}
      {step === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PRIORITY_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.key}
              icon={opt.icon}
              label={opt.label}
              hint={opt.hint}
              onClick={() => handlePrioritySelect(opt.key)}
            />
          ))}
        </div>
      )}

      {/* ── Schritt 3: Ergebnis ──────────────────────────────────────────── */}
      {step === 3 && recommendedPlan && (
        <div className="space-y-5">

          {/* Einordnungspfad */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 rounded-full text-sm font-medium text-stone-600">
              {audienceOption?.icon} {recommendation?.label}
            </span>
            <ChevronRight size={13} className="text-stone-400 flex-shrink-0" />
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-fox-fire/10 rounded-full text-sm font-semibold text-fox-fire">
              {recommendedPlan.name}
            </span>
          </div>

          {/* Empfohlener Plan */}
          <div className="rounded-2xl border-2 border-fox-fire bg-white p-6 shadow-sm shadow-orange-100">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0 mr-4">
                <h2 className="font-heading font-bold text-xl text-stone-900">
                  {recommendedPlan.name}
                </h2>
                <p className="text-sm text-stone-500 mt-1 leading-snug">
                  {recommendedPlan.description}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                {/* Anfrage-Stufen (Enterprise): priceLabel statt Festpreis */}
                {recommendedPlan.priceLabel ? (
                  <>
                    <p className="text-2xl font-bold text-stone-900 font-heading">
                      {recommendedPlan.priceLabel}
                    </p>
                    <p className="text-xs text-stone-400">auf Anfrage</p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-stone-900 font-heading">
                      {recommendedPlan.priceMonthly} €
                    </p>
                    <p className="text-xs text-stone-400">/Monat</p>
                  </>
                )}
              </div>
            </div>

            {/* Empfehlungs-Note aus audienceRecommendations */}
            {recommendation?.note && (
              <p className="text-sm text-stone-600 bg-stone-50 rounded-lg px-3 py-2.5 leading-relaxed border border-stone-100">
                {recommendation.note}
              </p>
            )}

            {/* Situativer Prioritäts-Hinweis */}
            {priorityNote && (
              <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2.5 mt-2 leading-relaxed border border-emerald-100">
                {priorityNote}
              </p>
            )}
          </div>

          {/* Empfohlene Add-ons */}
          {suggestedAddons.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2.5">
                Passende Add-ons für dich
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestedAddons.map((addon) => (
                  <span
                    key={addon.id}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-stone-200 rounded-xl text-sm font-medium text-stone-700"
                  >
                    <span className="text-base">{addon.icon}</span>
                    <span>{addon.name}</span>
                    <span className="text-xs text-stone-400 font-normal">
                      · +{addon.priceMonthly} €/Monat
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Trial-Hinweis */}
          {trialConfig.enabled && (
            <div className="flex items-start gap-2.5 text-sm bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
              <Check size={15} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <span className="text-stone-600 leading-snug">
                <strong className="text-emerald-700">
                  {trialConfig.days} Tage kostenlos testen
                </strong>
                {" — "}
                {trialConfig.disclaimer}
              </span>
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              onClick={handleGoToPricing}
              className="flex-1 py-3 px-6 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-fox-fire-dark transition-all shadow-md shadow-orange-100 flex items-center justify-center gap-2"
            >
              Plan ansehen & Testphase starten
              <ArrowRight size={15} />
            </button>
            <button
              onClick={handleReset}
              className="py-3 px-5 border-2 border-stone-200 rounded-xl text-sm font-medium text-stone-600 hover:border-stone-300 hover:bg-stone-50 transition-all flex items-center justify-center gap-1.5"
            >
              <RotateCcw size={13} />
              Neu starten
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
